import React, { useState } from 'react';
import { Phone, Mail, CheckCircle, Loader2, Send, DollarSign, Shield, TrendingUp, Users, Briefcase, Globe, Zap, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  { icon: DollarSign, title: 'Competitive Compensation', desc: 'Industry-leading commission splits with no caps. Your earning potential is unlimited.' },
  { icon: Globe, title: 'Nationwide Lending', desc: 'Originate loans across all 50 states. No geographic limitations on your business.' },
  { icon: Zap, title: 'Fast Closings', desc: 'Close deals in as few as 10 days. Speed wins deals — and earns referrals.' },
  { icon: Shield, title: 'Full Product Suite', desc: 'Hard money, fix & flip, DSCR, cash out refi, new construction, and commercial — all under one roof.' },
  { icon: TrendingUp, title: 'Marketing Support', desc: 'Branded marketing materials, lead generation tools, and CRM access to grow your pipeline.' },
  { icon: Award, title: 'Training & Mentorship', desc: 'Full training program, deal structuring support, and mentorship from experienced leadership. No prior lending experience needed.' },
];

const JoinPage = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', currentCompany: '',
    experience: '', licensedStates: '', requestType: 'info', message: ''
  });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/recruitment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to submit');
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', currentCompany: '', experience: '', licensedStates: '', requestType: 'info', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Join Our Team | Loan Officer Careers at Close Now Lending"
        description="Join Close Now Lending as a loan officer or real estate professional. No NMLS license required for business purpose loans. Competitive commissions, nationwide lending, full product suite, and training. Real estate agents welcome."
        keywords="loan officer jobs, real estate agent side income, business purpose loans, Close Now Lending careers, lending careers Houston, loan officer recruitment, no license required lending, real estate investor financing careers"
        url="/join"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80')] bg-cover bg-center opacity-15" />
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <span className="inline-block bg-blue-600 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded mb-6">
              Now Recruiting
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Build Your Lending Career With <span className="text-blue-400">Close Now Lending</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl leading-relaxed">
              We're looking for driven loan officers who want to take their production to the next level. Real estate agents looking to add an income stream are also a great fit. Because our loans are business purpose, <strong className="text-white">no NMLS license is required</strong>. We provide the training, products, and support to close more deals.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#apply">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3" data-testid="hero-apply-btn">
                  Apply Now
                </Button>
              </a>
              <a href="tel:+19034762937">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-3">
                  <Phone className="h-5 w-5 mr-2" /> Call 903-476-2937
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Loan Officers Choose Us</h2>
            <p className="text-gray-600">
              We've built a platform designed to help you succeed. From day one, you'll have everything you need to originate, process, and close loans faster than the competition.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((b, i) => (
              <Card key={i} className="border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <b.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We're Looking For */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We're Looking For</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                We seek motivated loan officers who thrive in a fast-paced environment and are passionate about helping real estate investors succeed. Real estate agents looking to supplement their income by learning the lending side are also a great fit. Since we focus on business purpose loans, <strong>no NMLS license is required</strong>. We'll train you on everything you need to know.
              </p>
              <ul className="space-y-4">
                {[
                  'Experience in private/hard money, commercial, or investment lending preferred',
                  'Strong relationship-building and communication skills',
                  'Self-motivated with a track record of closing deals or building client relationships',
                  'Ability to work independently while leveraging team resources',
                  'Real estate agents welcome — a great way to add income by helping investors secure financing',
                  'No NMLS license required — our business purpose loans are exempt from consumer lending regulations'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm border">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Our Loan Products</h3>
              <p className="text-gray-500 text-sm mb-6">Everything your borrowers need, under one roof:</p>
              <div className="grid grid-cols-2 gap-3">
                {['Hard Money', 'Fix & Flip', 'DSCR / Rental', 'Cash Out Refi', 'New Construction', 'Commercial', 'Multi-Family', 'Short Term Rental'].map((product) => (
                  <div key={product} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2.5">
                    <Briefcase className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{product}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500">Loan amounts from</p>
                <p className="text-2xl font-bold text-gray-900">$150K – $50M</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-white" id="apply">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Join?</h2>
              <p className="text-gray-600">
                Fill out the form below and our team will reach out within 24 hours. Choose whether you'd like more information or prefer to schedule a Zoom call to discuss opportunities.
              </p>
            </div>

            {submitted ? (
              <div data-testid="recruitment-success" className="bg-green-50 border border-green-200 rounded-xl p-10 text-center">
                <CheckCircle className="h-14 w-14 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Received!</h3>
                <p className="text-gray-600 mb-6">Thank you for your interest in joining Close Now Lending. Our team will be in touch within 24 hours.</p>
                <Button onClick={() => setSubmitted(false)} variant="outline" data-testid="submit-another-btn">Submit Another Application</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 bg-slate-50 rounded-2xl p-8 border" data-testid="recruitment-form">
                {/* Request Type */}
                <div>
                  <Label className="text-sm font-semibold text-gray-900 mb-3 block">I'd like to: *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.requestType === 'info' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    >
                      <input
                        type="radio"
                        name="requestType"
                        value="info"
                        checked={formData.requestType === 'info'}
                        onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                        className="accent-blue-600"
                        data-testid="request-type-info"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Request Information</p>
                        <p className="text-xs text-gray-500">Send me details about the opportunity</p>
                      </div>
                    </label>
                    <label
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.requestType === 'zoom' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    >
                      <input
                        type="radio"
                        name="requestType"
                        value="zoom"
                        checked={formData.requestType === 'zoom'}
                        onChange={(e) => setFormData({ ...formData, requestType: e.target.value })}
                        className="accent-blue-600"
                        data-testid="request-type-zoom"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Schedule a Zoom Call</p>
                        <p className="text-xs text-gray-500">Let's talk about the opportunity live</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Full Name *</Label>
                    <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jane Smith" className="mt-1" data-testid="recruit-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Email *</Label>
                    <Input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jane@email.com" className="mt-1" data-testid="recruit-email" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Phone *</Label>
                    <Input required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1" data-testid="recruit-phone" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Current Company</Label>
                    <Input value={formData.currentCompany} onChange={(e) => setFormData({ ...formData, currentCompany: e.target.value })} placeholder="ABC Lending" className="mt-1" data-testid="recruit-company" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Background</Label>
                    <select
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="recruit-experience"
                    >
                      <option value="">Select your background...</option>
                      <option value="real-estate-agent">Real Estate Agent</option>
                      <option value="loan-officer">Loan Officer / Originator</option>
                      <option value="mortgage-broker">Mortgage Broker</option>
                      <option value="real-estate-investor">Real Estate Investor</option>
                      <option value="sales-professional">Sales Professional</option>
                      <option value="other">Other / New to Lending</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">States You Operate In</Label>
                    <Input value={formData.licensedStates} onChange={(e) => setFormData({ ...formData, licensedStates: e.target.value })} placeholder="TX, CA, FL, NY..." className="mt-1" data-testid="recruit-states" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Additional Message</Label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your production, your goals, or any questions you have..."
                    className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="recruit-message"
                  />
                </div>

                {error && <p className="text-red-600 text-sm">{error}</p>}

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="sms-optin-join"
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    required
                    data-testid="join-sms-optin"
                  />
                  <label htmlFor="sms-optin-join" className="text-xs text-gray-500 leading-relaxed">
                    I agree to be contacted by Close Now Lending Corp via call, email, and text for financial services. To opt out of text messages, you can reply 'stop' at any time or reply 'help' for assistance. You can also select the unsubscribe link in the emails. Message and data rates may apply. Message frequency may vary. <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a> | <a href="/terms" className="text-blue-600 underline">Terms of Service</a>
                  </label>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base" data-testid="recruit-submit-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {submitting ? 'Submitting...' : formData.requestType === 'zoom' ? 'Request Zoom Appointment' : 'Request Information'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold text-white mb-4">Questions? Let's Talk.</h2>
          <p className="text-gray-300 mb-8">
            Call us directly to learn more about opportunities at Close Now Lending. We're happy to answer any questions.
          </p>
          <a href="tel:+19034762937">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-10">
              <Phone className="h-5 w-5 mr-2" /> Call 903-476-2937
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoinPage;
